import React, { useEffect, useMemo, useRef, useState } from 'react';
import { CommonBackContent } from '../Layouts/CommonBackContent';
import dynamic from 'next/dynamic';
import { InstagramAnonymouslyModel } from '@/types/anonymously.interface';
import CommonSimpleModal from '@/components/Common/CommonSimpleModal';

import { useTranslation } from 'next-i18next';
import { useUserProvider } from '@/context/UserProvider';
import {
  fetchInstagramAnonymouslySearchData,
  fetchInstagramAnonymouslyTimesData,
} from '@/lib/service/anonymously';
import { debounce } from 'lodash-es';

import { ga_logEvent } from '@/shared/ga';
import { EventName } from '@/shared/event-types';
import { message } from 'antd';
import { DownloadStoriesPreviewModel, SearchLimitLeftModel } from '@/types/home';
import { AccountLimitAlert, LimitAlert } from './FeeLimitAlert';
import { addInsStory } from '@/lib/service/dashboard';
import router from 'next/router';
import CustomersTalkUsContent from './CustomersTalkUsContent';
import LocalImage from '../Common/LocalImage';

const FAQContent = dynamic(() => import('./FAQContent'), { ssr: true });
const DownloadInstagramContent = dynamic(() => import('./DownloadInstagramContent'), { ssr: true });
const TopSearchContent = dynamic(() => import('./TopSearch'), { ssr: true });
const SearchResultContent = dynamic(() => import('./SearchResultContent'), { ssr: true });
const ChooseContent = dynamic(() => import('./ChooseContent'), { ssr: true });
const GeneralExampDownloadContent = dynamic(() => import('./GeneralExampDownloadContent'), {
  ssr: true,
});
const WhyDownloadStoriesContent = dynamic(() => import('./WhyDownloadStoriesContent'), {
  ssr: true,
});
const InstagramActivityTrackerContent = dynamic(() => import('./InstagramActivityTracker'), {
  ssr: true,
});
const BestTipsContent = dynamic(() => import('./BestTipsContent'), { ssr: true });
const DownloadStoriesBottomContent = dynamic(() => import('./DownloadStoriesBottomContent'), {
  ssr: true,
});
// const LocalImage = dynamic(() => import('../Common/LocalImage'), { ssr: false });
const LandingPagePricingContent = dynamic(() => import('./PricingContent'), { ssr: true });

const DownloadInstagramStoryContent = ({ data }: { data?: any }) => {
  const [contentData, setContentData] = useState<DownloadStoriesPreviewModel>(data?.contentData);
  const [currentModel, setCurrentModel] = useState<InstagramAnonymouslyModel>(null);
  const searchResultRef = useRef<HTMLDivElement>(null);
  const priceRef = useRef<HTMLDivElement>(null);
  const [alertInfo, setAlertInfo] = useState<any>(null);
  const { t } = useTranslation();
  const [errorCode, setErrorCode] = useState<string | number>('');

  const [limitLeftModel, setLimitLeftModel] = useState<SearchLimitLeftModel>({
    download_limit: 3,
    search_limit: 1,
    un_limit: false,
  });
  const [showLimitPage, setShowLimitPage] = useState<boolean>(false);
  const [showAccountLimit, setShowAccountLimit] = useState<boolean>(false);
  const { isLogin, onOpenLoginModal, onChangedLoginView } = useUserProvider();
  const [loading, setLoading] = useState(false);
  const [loginState, setLoginState] = useState(isLogin);
  const [searchStr, setSearchStr] = useState('');
  const [inputVisible, setInputVisible] = useState(true);
  const [insListLoading, setInsListLoading] = useState(false);

  const getNoStoriesDetectedContent = (img?: string, name?: string) => {
    return (
      <div style={{ paddingTop: '15px', marginBottom: '50px' }}>
        <p style={{ textAlign: 'center' }}>
          {t(
            'This account hasn’t posted any Stories in the last 24 hours. Add it to your tracking list for long-term monitoring',
          )}
        </p>
        <div
          style={{
            background: '#F1EFFF',
            borderRadius: '24px',
            padding: '20px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            marginTop: '20px',
          }}
        >
          <LocalImage
            src={img}
            style={{
              width: '60px',
              height: '60px',
              borderRadius: '50%',
              overflow: 'hidden',
            }}
          />
          <span style={{ marginLeft: '10px', fontWeight: '500' }}>{name ?? ''}</span>
        </div>
      </div>
    );
  };

  useEffect(() => {
    if (showLimitPage) {
      ga_logEvent(EventName.GA_search_now_limit_notify_displayed);
    }
  }, [showLimitPage]);

  useEffect(() => {
    setContentData(data?.contentData);
  }, [data]);

  const debouncedChangeHandler = useMemo(
    () =>
      debounce((value) => {
        setLoginState(value);
      }, 500),
    [],
  );

  useEffect(() => {
    return () => {
      debouncedChangeHandler.cancel();
    };
  }, [debouncedChangeHandler]);

  useEffect(() => {
    if (loginState) {
      fetchTimesRequest();
    } else {
      setLimitLeftModel({
        download_limit: 3,
        search_limit: 1,
        un_limit: false,
      });
    }
  }, [loginState]);

  useEffect(() => {
    setLoginState(isLogin);
  }, []);

  useEffect(() => {
    setLoginState(isLogin);
  }, [isLogin]);

  const fetchTimesRequest = () => {
    fetchInstagramAnonymouslyTimesData(null)
      .then((res) => {
        if (res?.code === 0) {
          setLimitLeftModel(res?.data);
        } else {
          setLimitLeftModel(null);
        }
      })
      .catch((error) => {
        setLimitLeftModel(null);
      })
      .finally(() => {});
  };

  const onValueChanged = (e?: string) => {
    setSearchStr(e ?? '');
  };

  const onSearch = (e?: string) => {
    if (e !== searchStr && !currentModel) {
      setCurrentModel(null);
    }
    setSearchStr(e ?? '');

    if (!isLogin) {
      onOpenLoginModal(true);
      onChangedLoginView(false);
      return;
    }
    onSearchClick(e);
  };

  const onSearchClick = (e?: string) => {
    if (e?.length > 0) {
      if (loading) {
        return;
      }
      setLoading(true);

      fetchInstagramAnonymouslySearchData(e, null)
        .then((res) => {
          if (res?.code === 0) {
            fetchTimesRequest();
            setCurrentModel(res?.data);
            // 如果账号没有数据，并且没有关联该账号，则提示没有数据
            if (!res?.data?.has_data && !res?.data?.has_add) {
              setAlertInfo({
                title: t('No Stories Detected'),
                desc: getNoStoriesDetectedContent(res?.data?.avatar, res?.data?.media_name),
                btnTitle: t('Add Account Now'),
                href: `/plan?media_name=${res?.data?.media_name}&avatar=${res?.data?.avatar}&media_url=${res?.data?.media_url}`,
                type: 'No-Stories-Detected',
                media_name: res?.data?.media_name,
                avatar: res?.data?.avatar,
                media_url: res?.data?.media_url,
              });
              return;
            }

            setCurrentModel(res?.data);
            ga_logEvent(EventName.GA_one_click_displayed);

            if (searchResultRef.current) {
              const targetTop = searchResultRef.current.offsetTop - 220;
              window.scrollTo({
                top: targetTop,
                behavior: 'smooth',
              });
            }
          } else if (res?.code === 22001 || res?.code === 22006) {
            fetchTimesRequest();
            setShowLimitPage(true);
          } else if (res?.code === 22004 || res?.code === 22006) {
            // 隐私账号走这里
            setErrorCode(res?.code);
            setCurrentModel(null);
          } else {
            message.error(res?.message);
          }
        })
        .catch((error) => {})
        .finally(() => {
          setLoading(false);
        });
    } else {
      message.error(t('Please enter the insert link or username'));
    }
  };

  const onAddAccountToTrackClick = (media_name?: string) => {
    if (insListLoading) {
      return;
    }
    setInsListLoading(true);

    addInsStory(media_name)
      ?.then((res) => {
        if (res?.code === 0) {
          message.success(t('Added successfully'));
          router.push('/dashboard?dashboard_type=toolkit');
        } else {
          if (res?.code !== 22008) {
            message.error(res?.message);
          }

          if (res?.code === 22007) {
            setShowLimitPage(true);
          } else if (res?.code === 22008) {
            setShowAccountLimit(true);
          }
        }
      })
      .finally(() => {
        setInsListLoading(false);
      });
  };

  const gotoPlanClick = () => {
    if (priceRef.current) {
      const targetTop = priceRef.current.offsetTop - 60;
      window.scrollTo({
        top: targetTop,
        behavior: 'smooth',
      });
    }
  };

  return (
    <>
      <CommonBackContent>
        <TopSearchContent
          topInfo={contentData?.contentInfo?.hero}
          loading={loading}
          leftModel={limitLeftModel}
          searchVal={searchStr}
          onSearchClick={(e) => {
            onSearch(e);
            ga_logEvent(EventName.GA_search_now_clicks);
          }}
          onValueChanged={onValueChanged}
          onInputVisibleChanged={(visible) => setInputVisible(visible)}
          onUnlimitedClick={gotoPlanClick}
          errorCode={errorCode}
          resetErrorCode={() => setErrorCode('')}
        />

        <div ref={searchResultRef}>
          {currentModel && (
            <SearchResultContent
              tabFunc={data?.contentData?.function_child}
              loading={insListLoading}
              post_page_token={currentModel?.post_page_token}
              data={currentModel}
              onAddAccountToTrackClick={onAddAccountToTrackClick}
              // onPlanClick={gotoPlanClick}
              onPlanClick={() => setShowLimitPage(true)}
            />
          )}
        </div>

        <DownloadInstagramContent data={contentData?.contentInfo['section-1']} />
        <ChooseContent data={contentData?.contentInfo['section-2']} />
        <GeneralExampDownloadContent data={contentData?.contentInfo['section-3']} />
        <WhyDownloadStoriesContent data={contentData?.contentInfo['section-4']} />
        <div ref={priceRef}>
          <LandingPagePricingContent func="story" />
        </div>
        <InstagramActivityTrackerContent
          contentData={contentData}
          isTracker={true}
          webBg={
            'https://res-front.pumpsoul.com/ins-prod/image/activity_tracker_bg_web_0110_1.webp'
          }
          phoneBg={
            'https://res-front.pumpsoul.com/ins-prod/image/activity_tracker_bg_phone_0109.webp'
          }
        />

        <CustomersTalkUsContent contentInfo={contentData?.contentInfo?.['section-user-reviews']} />

        {contentData?.faqList && (
          <FAQContent
            faqInfo={contentData?.contentInfo?.['section-faq']}
            faqList={contentData?.faqList}
          />
        )}
        <BestTipsContent
          title={contentData?.contentInfo?.['section-top-blog']?.title}
          blogList={contentData?.topBlogList}
        />
        <BestTipsContent
          title={contentData?.contentInfo?.['section-blog']?.title}
          blogList={contentData?.blogList}
        />
      </CommonBackContent>

      <DownloadStoriesBottomContent
        bottomInfo={contentData?.contentInfo['section-buttom']}
        loading={loading}
        searchVal={searchStr}
        inputVisible={inputVisible}
        onValueChanged={onValueChanged}
        onSearchClick={(e) => {
          window.scrollTo({ top: 220 });
          onSearch(e);
          ga_logEvent(EventName.GA_add_tracking_button_clicks);
        }}
      />

      <CommonSimpleModal
        open={alertInfo !== null}
        title={alertInfo?.title}
        desc={alertInfo?.desc}
        btnTitle={alertInfo?.btnTitle}
        closeAction={(isOk) => {
          if (isOk && alertInfo?.href) {
            if (alertInfo.type == 'No-Stories-Detected') {
              setAlertInfo(null);
              router.push(
                `${router.asPath}?media_name=${alertInfo?.media_name}&avatar=${alertInfo?.avatar}&media_url=${alertInfo?.media_url}#1`,
              );
              return;
            }
            router.push(alertInfo?.href);
          }
          if (isOk) {
            if ('Out-of-Views' === alertInfo?.type) {
              ga_logEvent(EventName.ViewInstagramStoriesAnonymously_OutOfViews_AddAccountNow);
            }
          } else {
            setAlertInfo(null);
          }
        }}
      />

      <LimitAlert
        open={showLimitPage}
        closeAction={(isOk) => {
          setShowLimitPage(false);

          if (isOk) {
            ga_logEvent(EventName.GA_search_now_limit_notify_clicked);
            setTimeout(() => {
              gotoPlanClick();
            }, 20);
          }
        }}
      />

      <AccountLimitAlert
        open={showAccountLimit}
        closeAction={(isOk) => {
          setShowAccountLimit(false);

          if (isOk) {
            ga_logEvent(EventName.GA_account_limit_reached_notify_clicked);
            setTimeout(() => {
              gotoPlanClick();
            }, 20);
          }
        }}
      />
    </>
  );
};

export default DownloadInstagramStoryContent;
